import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import style from './FormManagementTable.module.scss';
import InitTable, { Query } from '../Table/Table';
import Loader, { LoaderIndicator } from '../Loader/Loader';
import { TOOLTIP_SECTIONS } from '../../enums';
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper';
import FieldSelect from '../FieldSelect/FieldSelect';
import { FormikProps, FormikValues, useFormik } from 'formik';
import { InputType } from '../Table/components/Editor/Editor';
import FieldToggleCheckbox from '../FieldToggleCheckbox/FieldToggleCheckbox';
import Divider from '../Divider/Divider';
import _ from 'lodash';
import { IBackendFormMapping } from '../../interface/IBackendFormMapping';
import { getCommonAssetClass, getDepreciationSchedules, getFormGroups, getIndexTables } from '../../services';
import { SelectOption } from '../../interface';

const { Table, Column } = InitTable<IBackendFormMapping>();

interface IUpdateSelectedAssetsForm {
  formGroup?: number | null;
  depreciationSchedule?: number | null;
  indexTable?: number | null;
  taxable?: boolean;
  reportable?: boolean;
}

interface Props {
  className?: string;
  setVisibleFilterPanel?: React.Dispatch<React.SetStateAction<"filter" | null>>;
  visibleFilterPanel?: "filter" | null;
  onSaveDataSuccess?: () => void;
  formMappingsList?: IBackendFormMapping[];
  searchStandardAssetClass?: string;
  formId?: number | null;
  depreciationScheduleCollectionId?: number | null;
  indexTableCollectionId?: number | null;
  updateUnsavedFormMappingsList?: (newValues: IBackendFormMapping[]) => void;
  saveMappingInfo?: (newValues: IBackendFormMapping[]) => void;
}

const initialValues = {
  formGroup: null,
  depreciationSchedule: null,
  indexTable: null,
  taxable: false,
  reportable: false,
} as IUpdateSelectedAssetsForm;

function FormManagementTable({
  className,
  setVisibleFilterPanel,
  visibleFilterPanel,
  formMappingsList,
  searchStandardAssetClass,
  formId,
  depreciationScheduleCollectionId,
  indexTableCollectionId,
  updateUnsavedFormMappingsList,
  saveMappingInfo
}: Props) {
  const [isSaving] = useState(false)

  const [selected, setSelected] = useState<IBackendFormMapping[]>([]);
  const [commonAssetClassOptions, setCommonAssetClassesOptions] = useState<SelectOption[]>([]);
  const [formGroupOptions, setFormGroupOptions] = useState<SelectOption[]>([]);
  const [depreciationScheduleOptions, setDepreciationScheduleOptions] = useState<SelectOption[]>([]);
  const [indexTableOptions, setIndexTableOptions] = useState<SelectOption[]>([]);
  const [tableQuery, setTableQuery] = useState<Query>({});

  const [fetchingCommonAssetClass, setFetchingCommonAssetClass] =
    useState(false);
  const [fetchingFormGroup, setFetchingFormGroup] =
      useState(false);
  const [fetchingDepreciationSchedule, setFetchingDepreciationSchedule] =
      useState(false);
  const [fetchingIndexTable, setFetchingIndexTable] =
      useState(false);

  useEffect(() => {
    setFetchingCommonAssetClass(true);
    getCommonAssetClass()
      .then((res) => {
        setCommonAssetClassesOptions(res.data.map(item => ({
          value: item.id,
          label: item.name,
          name: item.name
        })));
        setFetchingCommonAssetClass(false);
      })
      .catch(() => {
        setFetchingCommonAssetClass(false);
      });
  }, []);

  useEffect(() => {
    if (formId) {
      setFetchingFormGroup(true);
      getFormGroups({
        formId,
        page: 0
      })
        .then((res) => {
          setFormGroupOptions(res.data.items.map(item => ({
            value: item.id,
            label: item.name,
            name: item.name
          })));
          setFetchingFormGroup(false);
        })
        .catch(() => {
          setFetchingFormGroup(false);
        });
    } else {
      setFormGroupOptions([]);
    }
  }, [formId]);

  useEffect(() => {
    if (depreciationScheduleCollectionId) {
      setFetchingDepreciationSchedule(true);
      getDepreciationSchedules({
        depreciationScheduleCollectionId,
        page: 0
      })
        .then((res) => {
          setDepreciationScheduleOptions(res.data.items.map(item => ({
            value: item.id,
            label: item.name,
            name: item.name
          })));
          setFetchingDepreciationSchedule(false);
        })
        .catch(() => {
          setFetchingDepreciationSchedule(false);
        });
    } else {
      setDepreciationScheduleOptions([]);
    }
  }, [depreciationScheduleCollectionId]);

  useEffect(() => {
    if (indexTableCollectionId) {
      setFetchingIndexTable(true);
      getIndexTables({
        indexTableCollectionId,
        page: 0
      })
        .then((res) => {
          setIndexTableOptions(res.data.items.map(item => ({
            value: item.id,
            label: item.name,
            name: item.name
          })));
          setFetchingIndexTable(false);
        })
        .catch(() => {
          setFetchingIndexTable(false);
        });
    } else {
      setIndexTableOptions([]);
    }
  }, [indexTableCollectionId]);

  function searchData(
    datas: IBackendFormMapping[],
    search: string | undefined,
    filter: {
      [keyOrProp: string]: unknown;
    } | undefined
  ) {
    const results = _.filter(datas, (item) => {
      if (!filter) {
        return true;
      }
      return Object.keys(filter).every(key => {
        return _.get(filter, key) == _.get(item, key);
      });
    }) as IBackendFormMapping[];
    if (search) {
      const escaped = _.escapeRegExp(search);
      return _.filter(
        results,
        (item) => {
          const selectedValue = _.find(commonAssetClassOptions, { value: item.commonClassId });
          return new RegExp(escaped, 'i').test(`${selectedValue?.name ?? item.commonClassId}`)
        }
      );
    } else {
      return results;
    }
  }
  useEffect(() => {
    setSelected([]); // clear selected if data change
  }, [formMappingsList]);

  const filterdItems = useMemo(() => {
    return searchData(
      _.orderBy(
        formMappingsList,
        _.last(_.keys(tableQuery?.order)),
        _.last(_.values(tableQuery?.order)),
      ),
      searchStandardAssetClass,
      tableQuery?.filter
    );
  }, [
    formMappingsList,
    tableQuery?.filter,
    tableQuery?.order,
    searchStandardAssetClass
  ]);

  const items = useMemo(() => {
    return _.nth(
      _.chunk(
        filterdItems,
        tableQuery?.pagination?.pageSize,
      ),
      (tableQuery?.pagination?.page as number) - 1,
    ) as IBackendFormMapping[];
  }, [
    filterdItems,
    tableQuery?.pagination
  ]);

  const formik: FormikProps<IUpdateSelectedAssetsForm> = useFormik<FormikValues>({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: (values: IUpdateSelectedAssetsForm) => {
      saveMappingInfo?.(selected.map(item => ({
        ...item,
        formGroupId: values.formGroup ?? null,
        depreciationScheduleId: values.depreciationSchedule ?? null,
        indexTableId: values.indexTable ?? null,
        taxable: values.taxable,
        reportable: values.reportable
      })));
    },
  });
  useEffect(() => {
    if (!selected.length) {
      formik.setValues(initialValues);
    }
  }, [selected])

  const isFormChanged = useMemo(() => !_.isEqual(
    initialValues,
    formik.values
  ), [formik.values, initialValues]);

  const getData = useCallback((query?: Query) => {
    if (query) {
      setTableQuery(query)
    }
  }, []);

  const saveChanges = (newValue: IBackendFormMapping) => {
    updateUnsavedFormMappingsList?.([newValue]);
  };

  return (
    <div className={
      classNames(
        className,
        style['main-content'],
        'FormManagementSearch d-flex flex-column'
      )
    }>
      {selected.length > 0 && (
        <div className={style.blockSelected}>
          <Divider className={style.divider1} />
          <div
            className='d-flex align-items-end full-width gap-16 justify-content-between flex-wrap'
          >
            <div className='d-flex align-items-end gap-16 flex-wrap'>
              <button
                className={style.textSelectedContainer}
                onClick={() => {
                  setSelected([]);
                }}
              >
                <span className={style.textSelected}>Selected Asset Class ({selected.length})</span>
                <span className={style.textUnselected}>Unselected Asset Class ({selected.length})</span>
              </button>
              <FieldSelect
                labelText='Form Group'
                options={formGroupOptions}
                selectId={formik.values.formGroup}
                onSelect={(value) => {
                  formik.setFieldValue(
                    'formGroup',
                    _.toNumber(value?.value),
                  )
                }}
                classnames={style['width-130']}
              />

              <FieldSelect
                labelText='Depreciation Schedule'
                options={depreciationScheduleOptions}
                selectId={formik.values.depreciationSchedule}
                onSelect={(value) => {
                  formik.setFieldValue(
                    'depreciationSchedule',
                    _.toNumber(value?.value),
                  )
                }}
                classnames={style['width-130']}
              />

              <FieldSelect
                labelText='Index Table'
                options={indexTableOptions}
                selectId={formik.values.indexTable}
                onSelect={(value) => {
                  formik.setFieldValue(
                    'indexTable',
                    _.toNumber(value?.value),
                  )
                }}
                classnames={style['width-130']}
              />

              <FieldToggleCheckbox
                label='Taxable'
                value={formik.values.taxable ?? false}
                onChange={(newVal) => {
                  formik.setFieldValue(
                    'taxable',
                    newVal,
                  )
                }}
                classnames={style.FieldToggleCheckbox}
              />

              <FieldToggleCheckbox
                label='Reportable'
                value={formik.values.reportable ?? false}
                onChange={(newVal) => {
                  formik.setFieldValue(
                    'reportable',
                    newVal,
                  )
                }}
                classnames={style.FieldToggleCheckbox}
              />
            </div>

            <div className='d-flex gap-12'>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Save'
              >
                <button
                  className='primary'
                  disabled={isSaving || !isFormChanged}
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  Save
                  {isSaving ? (
                    <LoaderIndicator
                      className='button-loading'
                      loading={true}
                    />
                  ) : null}
                </button>
              </TooltipWrapper>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Cancel'
              >
                <button
                  className='secondary'
                  onClick={() => {
                    setSelected([]);
                  }}
                >
                  Cancel
                </button>
              </TooltipWrapper>
            </div>
          </div>
        </div>
      )}

      <Table
        id='FormManagementTable'
        rows={items}
        onQueryChanged={getData}
        paginate
        sortable
        selectable={{ selected, onChange: setSelected }}
        rowId='commonClassId'
        totalRows={filterdItems?.length}
        loading={false}
        showTopBar={false}
        forceVisiblePanel={visibleFilterPanel}
        closeVisiblePanel={() => setVisibleFilterPanel?.(null)}
      >
        <Column
          label='Standard Asset Class'
          prop='commonClassId'
          filterable={{
            type: InputType.Select,
            options: commonAssetClassOptions,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            multi: false,
            searchable: true,
          }}
          accessor={(rowInfo) => {
            const selectedValue = _.find(commonAssetClassOptions, { value: rowInfo.commonClassId });
            if (selectedValue) {
              return selectedValue.name ?? '';
            }
            return rowInfo.commonClassId ?? '';
          }}
        ></Column>
        <Column
          label='Form Group'
          prop='formGroupId'
          editable
          filterable={{
            type: InputType.Select,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            options: formGroupOptions,
            multi: false,
            searchable: true,
          }}
          editor={{
            type: InputType.Select,
            options: formGroupOptions,
            nullOption: false,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            disabled: isSaving,
          }}
          onChange={(newValue, row) => {
            saveChanges({
              ...row,
              formGroupId: newValue,
            })
          }}
        ></Column>
        <Column
          label='Depreciation Schedule'
          prop='depreciationScheduleId'
          editable
          filterable={{
            type: InputType.Select,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            options: depreciationScheduleOptions,
            multi: false,
            searchable: true,
          }}
          editor={{
            type: InputType.Select,
            options: depreciationScheduleOptions,
            nullOption: false,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            disabled: isSaving,
          }}
          onChange={(newValue, row) => {
            saveChanges({
              ...row,
              depreciationScheduleId: newValue,
            })
          }}
        ></Column>
        <Column
          label='Index Table'
          prop='indexTableId'
          editable
          filterable={{
            type: InputType.Select,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            options: indexTableOptions,
            multi: false,
            searchable: true,
          }}
          editor={{
            type: InputType.Select,
            options: indexTableOptions,
            nullOption: false,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            disabled: isSaving,
          }}
          onChange={(newValue, row) => {
            saveChanges({
              ...row,
              indexTableId: newValue,
            })
          }}
        ></Column>
        <Column
          label="Taxable"
          prop='taxable'
          filterable
          editable
          editor={{
            type: InputType.Toggle,
          }}
          onChange={(newValue, row) => {
            saveChanges({
              ...row,
              taxable: newValue,
            })
          }}
        />
        <Column
          label="Reportable"
          prop='reportable'
          filterable
          editable
          editor={{
            type: InputType.Toggle,
          }}
          onChange={(newValue, row) => {
            saveChanges({
              ...row,
              reportable: newValue,
            })
          }}
        />
      </Table>

      <Loader
        isOpen={
          fetchingCommonAssetClass ||
          fetchingFormGroup ||
          fetchingDepreciationSchedule ||
          fetchingIndexTable
        }
      />
    </div>
  );
}

export default FormManagementTable;